import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useDeepEqualSelector } from 'Shared/utils/hooks';
import { useMembers } from 'Shared/utils/hooks';

import { transferWorkspace } from 'Shared/actions/workspaces';
import WpButton from 'Wpds/components/button/WpButton';
import WpButtonGroup from 'Wpds/components/button/WpButtonGroup';
import { WpModal, WpModalHeader, WpModalBody, WpModalFooter } from 'Wpds/components/modal';
import WpUserInput, { WidthType } from 'Wpds/components/user-input/WpUserInput';
import WpToastManager from 'Wpds/components/toast/WpToastManager';

type Props = {
  onToggle(): void;
};

function TransferWorkspaceModal({ onToggle }: Props) {
  const dispatch = useDispatch();

  const [fromUserId, setFromUserId] = useState(null);
  const [toUserId, setToUserId] = useState(null);

  const workspaceMembers = useDeepEqualSelector(state => state.workspace.users || []);
  const { isLoading: isLoadingMembers } = useMembers();

  const organizationMembers = useDeepEqualSelector(state => state.members || []);
  const organizationMembersNotInWorkspace = organizationMembers.filter(
    member => !workspaceMembers.find(workspaceMember => workspaceMember.id === member.id)
  );

  const [isSubmitting, setIsSubmitting] = useState(false);
  const handleCancelClick = () => onToggle();
  const workspaceId = useSelector(state => state.workspace.id);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    const response = await dispatch(
      transferWorkspace({
        workspaceId,
        fromUserId: organizationMembers.find(({ id }) => id === fromUserId)?.slug,
        toUserId: organizationMembers.find(({ id }) => id === toUserId)?.slug,
      })
    );
    if (response?.status === 200) WpToastManager.success('The workspace was successfully transferred!');
    else setIsSubmitting(false);
  };

  const handleNewFromUserSelect = fromUser => {
    setFromUserId(fromUser?.id);
  };
  const handleNewToUserSelect = toUser => {
    setToUserId(toUser?.id);
  };

  const isSubmitDisabled = isSubmitting || !fromUserId || !toUserId;
  if (workspaceMembers.length === 0) return null;
  if (isLoadingMembers) return null;
  return (
    <WpModal isOpen size="md" onToggle={onToggle}>
      <WpModalHeader>Transfer 1:1 Workspace</WpModalHeader>
      <WpModalBody>
        <div className="TransferWorkspaceModal">
          <div>
            User to remove
            <div>
              <WpUserInput
                users={workspaceMembers}
                onChange={handleNewFromUserSelect}
                handleRemoveUser={null}
                removeUserLabel="Unassign"
                placeholder="Select user"
                id="transferFromUserSelect"
                label="Owner"
                value={fromUserId}
                showSearch={false}
                labelHidden
                optionsWidth={WidthType.target}
              />
            </div>
          </div>
          <p />
          <div>
            User to add
            <div>
              <WpUserInput
                users={organizationMembersNotInWorkspace}
                onChange={handleNewToUserSelect}
                handleRemoveUser={null}
                removeUserLabel="Unassign"
                placeholder="Select user"
                id="transferToUserSelect"
                label="Owner"
                value={toUserId}
                labelHidden
                optionsWidth={WidthType.target}
              />
            </div>
          </div>
        </div>
        <div className="mt-4 text-muted">
          • Clicking transfer will remove the first user from the workspace and add the second user to the workspace.
          <p />
          • Incomplete action items and discussion topics assigned to the removed user will become unassigned.
          <p />
          • Goals in the workspace owned by the removed user will be moved to their personal workspace.
          <p />• All feedback in the workspace will remain in the workspace and be visible to the added user.
        </div>
      </WpModalBody>
      <WpModalFooter>
        <WpButtonGroup>
          <WpButton variant="tertiary" onClick={handleCancelClick}>
            Cancel
          </WpButton>
          <WpButton onClick={handleSubmit} disabled={isSubmitDisabled} loading={isSubmitting}>
            Transfer
          </WpButton>
        </WpButtonGroup>
      </WpModalFooter>
    </WpModal>
  );
}

export default TransferWorkspaceModal;
